import { useCallback, useEffect, useRef } from 'react'

import { CameraViewOrientation, FrontCameraProfile } from '@editor/interfaces/camera'
import { RootState } from '@editor/store/app'
import { useSelector } from 'react-redux'
import { Matrix4, Quaternion, Vector3 } from 'three'

import { BlueprintEditorContextState } from '../contexts/BlueprintEditorContext'
import { Mode } from '../definitions'

// Re-usable vector instances to avoid creating new ones
const perspectivePositions: Record<CameraViewOrientation, Vector3> = {
  front: new Vector3(),
  back: new Vector3(),
  top: new Vector3(),
  bottom: new Vector3(),
  left: new Vector3(),
  right: new Vector3(),
}

const useConfigureCanvas = (editorContext: BlueprintEditorContextState) => {
  // Hooks
  const { perspectiveCamera, orthographicCamera, activeCamera, arcballControls } = editorContext

  // States
  const selectedBlueprint = useSelector((state: RootState) => state.blueprint.editor.selectedBlueprint)
  const initCompleted = useSelector((state: RootState) => state.blueprint.editor.initCompleted)

  // Refs
  const cameraProfileRef = useRef<FrontCameraProfile | null>(null)

  useEffect(() => {
    cameraProfileRef.current = selectedBlueprint?.camera_profile || null
  }, [selectedBlueprint])

  /**
   * Setup the controls based on the current mode.
   *
   * @param md The mode to switch to. If not provided, it will use the context mode.
   */
  const setupControls = useCallback(
    (mode: Mode) => {
      if (!arcballControls?.current) return

      if (mode === Mode.Editor2D) {
        activeCamera.current = orthographicCamera.current
        arcballControls.current.enableRotate = false
      } else {
        activeCamera.current = perspectiveCamera.current
        arcballControls.current.enableRotate = true
      }

      if (arcballControls?.current && activeCamera.current !== arcballControls.current.object) {
        arcballControls.current.setCamera(activeCamera.current)
      }
    },
    [arcballControls, activeCamera, orthographicCamera, perspectiveCamera],
  )

  /**
   * Orient the perspective camera based on the selected blueprint's camera profile.
   */
  const orientPersp = useCallback(
    (orientation: CameraViewOrientation) => {
      // Camera profile can be missing if a dummy blueprint is added
      if (!cameraProfileRef.current || !arcballControls.current || !activeCamera.current) return

      const {
        target,
        positions,
        state: {
          arcballState: {
            cameraMatrix: { elements: cameraMatrix },
          },
        },
      } = cameraProfileRef.current

      const cameraPosition = new Vector3(...positions[CameraViewOrientation.FRONT])
      const cameraMatrixUp = new Vector3(...cameraMatrix.slice(4, 7))

      const targetV3 = new Vector3(...target)
      const direction = new Vector3().subVectors(cameraPosition, targetV3).normalize()
      const left = new Vector3().crossVectors(cameraMatrixUp, direction).normalize()
      const up = new Vector3().crossVectors(direction, left).normalize()
      const distance = cameraPosition.distanceTo(targetV3)
      const matrix = new Matrix4().fromArray(cameraMatrix)
      const quaternion = new Quaternion().setFromRotationMatrix(matrix)

      // Calculate all perspectives
      perspectivePositions.front.set(...cameraProfileRef.current.positions[CameraViewOrientation.FRONT])
      perspectivePositions.back.copy(direction).negate().multiplyScalar(distance).add(targetV3)
      perspectivePositions.top.copy(up).multiplyScalar(distance).add(targetV3)
      perspectivePositions.bottom.copy(up).negate().multiplyScalar(distance).add(targetV3)
      perspectivePositions.left.copy(left).negate().multiplyScalar(distance).add(targetV3)
      perspectivePositions.right.copy(left).multiplyScalar(distance).add(targetV3)

      const position = perspectivePositions[orientation]

      // Update controls with target as well as intended position of camera.
      // This will ensure all internal state knows where the target/camera is,
      // otherwise the controls will be confused and not work properly.
      // eg: user interaction puts the camera far away from the target, then
      //     when we update camera only which places it closer to the target, it
      //     will still behave as if the camera is still far away from the target.

      /* eslint-disable no-underscore-dangle */
      if (orientation === CameraViewOrientation.FRONT) {
        arcballControls.current._cameraMatrixState.compose(position, quaternion, activeCamera.current.scale)
      } else {
        if (orientation === CameraViewOrientation.TOP) {
          cameraMatrixUp.copy(perspectivePositions.back.clone().sub(targetV3).normalize())
        } else if (orientation === CameraViewOrientation.BOTTOM) {
          cameraMatrixUp.copy(perspectivePositions.front.clone().sub(targetV3).normalize())
        }

        const m2 = new Matrix4()
          .compose(position, quaternion, activeCamera.current.scale)
          .lookAt(position, targetV3, cameraMatrixUp)
        arcballControls.current._cameraMatrixState.copy(m2)
      }

      activeCamera.current.up.set(cameraMatrixUp.x, cameraMatrixUp.y, cameraMatrixUp.z)
      arcballControls.current._cameraMatrixState.decompose(
        activeCamera.current.position,
        activeCamera.current.quaternion,
        activeCamera.current.scale,
      )
      arcballControls.current._gizmoMatrixState.setPosition(targetV3)
      arcballControls.current._gizmoMatrixState.decompose(
        arcballControls.current._gizmos.position,
        arcballControls.current._gizmos.quaternion,
        arcballControls.current._gizmos.scale,
      )
      arcballControls.current.update()
      /* eslint-enable no-underscore-dangle */
    },
    [cameraProfileRef, arcballControls, activeCamera],
  )

  /**
   * Orient the orthographic camera based on the selected blueprint's camera profile.
   */
  const orientOrthographicCamera = useCallback(
    (profile: FrontCameraProfile, orientation: CameraViewOrientation = CameraViewOrientation.FRONT) => {
      // Camera profile can be missing if a dummy blueprint is added
      if (!arcballControls.current || !orthographicCamera.current) return

      const {
        target,
        positions,
        state: {
          arcballState: {
            cameraMatrix: { elements: cameraMatrix },
            cameraZoom,
          },
        },
      } = profile

      // Calculate all perspectives (there's only FRONT for orthographic)
      perspectivePositions.front.set(...positions[CameraViewOrientation.FRONT])
      const position = perspectivePositions[orientation]

      // Update controls with target as well as intended position of camera.
      // This will ensure all internal state knows where the target/camera is,
      // otherwise the controls will be confused and not work properly.
      // eg: user interaction puts the camera far away from the target, then
      //     when we update camera only which places it closer to the target, it
      //     will still behave as if the camera is still far away from the target.

      const targetV3 = new Vector3(...target)
      const cameraMatrixUp = new Vector3(...cameraMatrix.slice(4, 7))
      const direction = new Vector3().subVectors(position, targetV3).normalize()
      const left = new Vector3().crossVectors(cameraMatrixUp, direction).normalize()
      const up = new Vector3().crossVectors(direction, left).normalize()

      orthographicCamera.current.position.copy(position)
      orthographicCamera.current.lookAt(targetV3)
      orthographicCamera.current.up.set(up.x, up.y, up.z)
      orthographicCamera.current.zoom = cameraZoom
      orthographicCamera.current.updateMatrixWorld()
      arcballControls.current.setCamera(orthographicCamera.current)
      arcballControls.current.target.copy(targetV3)
      arcballControls.current.update()
    },
    [arcballControls, orthographicCamera],
  )

  const orientOrtho = useCallback(
    (orientation: CameraViewOrientation) => {
      // Camera profile can be missing if a dummy blueprint is added
      if (!cameraProfileRef.current) return

      orientOrthographicCamera(cameraProfileRef.current, orientation)
    },
    [cameraProfileRef, orientOrthographicCamera],
  )

  /**
   * Orient the camera based on the selected blueprint's camera profile.
   *
   * @param orientation The orientation to set the camera to. Defaults to FRONT.
   */
  const orientCamera = useCallback(
    (mode: Mode, orientation: CameraViewOrientation = CameraViewOrientation.FRONT) => {
      // Camera profile can be missing if a dummy blueprint is added
      if (
        !cameraProfileRef.current ||
        !arcballControls.current ||
        !activeCamera.current ||
        !perspectiveCamera.current ||
        !orthographicCamera.current ||
        !initCompleted
      )
        return

      if (mode === Mode.Editor3D) {
        orientPersp(orientation)
        return
      }

      orientOrtho(orientation)
    },
    [
      cameraProfileRef,
      activeCamera,
      perspectiveCamera,
      orthographicCamera,
      arcballControls,
      initCompleted,
      orientPersp,
      orientOrtho,
    ],
  )

  return { setupControls, orientCamera, orientOrthographicCamera }
}

export default useConfigureCanvas
